import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, Rocket, Clock, Printer, Mail, MessageSquare, AlertTriangle } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { dispatchSession } from '@/lib/kiosk';
import PosReceiptCard from './PosReceiptCard';
import { buildReceipt, money } from '@/lib/posReceipt';

const GREEN_BTN =
  'flex-1 bg-green-600 hover:bg-green-500 disabled:bg-white/5 disabled:text-white/25 text-white font-black text-sm tracking-widest py-5 flex items-center justify-center gap-2 transition-colors';

/**
 * The venue POS success screen. Payment is already taken — this confirms the
 * sale, shows the VAT receipt, and lets staff start the pre-selected bay now or
 * leave the booking for the kiosk queue. Nothing starts automatically.
 */
export default function WalkInSuccessModal({ result, payload, onNewSale }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [dispatching, setDispatching] = useState(false);
  const [parking, setParking] = useState(false);
  const [error, setError] = useState('');

  const [email, setEmail] = useState(result.customerEmail || payload.customerEmail || '');
  const [sending, setSending] = useState(false);
  const [invoicedTo, setInvoicedTo] = useState(
    result.invoiced ? result.customerEmail || payload.customerEmail || '' : ''
  );
  const [phone, setPhone] = useState(payload.customerPhone || '');
  const [smsSending, setSmsSending] = useState(false);
  const [textedTo, setTextedTo] = useState('');
  // The customer account the sale was matched to, shown once a receipt is sent.
  const [linkedAccount, setLinkedAccount] = useState('');

  const { data: rigs = [] } = useQuery({
    queryKey: ['pos-success-rigs'],
    queryFn: () => base44.entities.BookingRig.list(),
  });
  const rig = rigs.find((r) => r.key === payload.rigType) || null;

  // An extension-only sale sells time on a rig that is already running, so it
  // carries no new experience, no bay to dispatch and no booking to start.
  const extensionOnly = !!payload.extensionOnly;
  // Nothing behind this sale but the items themselves — extra time on a running
  // rig, or a straight retail basket. Either way there is no experience card, no
  // bay to dispatch and no session to start.
  const noSession = !payload.experienceType && !payload.experienceName;
  const extended = result.extensions?.extended || [];
  const extensionSkipped = result.extensions?.skipped || [];

  // The real VAT invoice number is the customer-facing reference. Only a sale
  // with no invoice behind it (an extension-only sale) falls back to the
  // derived order reference.
  const reference = result.invoiceNumber || result.orderRef;

  const addonsTotal = Number(payload.addonsTotal) || 0;
  const extensionsTotal = Number(payload.extensionsTotal) || 0;
  const receipt = buildReceipt({
    experienceName: noSession ? '' : payload.experienceName || payload.experienceType,
    experiencePrice: noSession ? 0 : Math.max(0, Number(payload.amount) - addonsTotal - extensionsTotal),
    // Extensions print as their own lines alongside any concessions.
    addons: [...(payload.addons || []), ...(payload.extensions || [])],
    amount: payload.amount,
  });

  const handleDispatch = async () => {
    if (!rig) {
      setError('The pre-selected bay is no longer available — dispatch from the kiosk instead.');
      return;
    }
    setDispatching(true);
    setError('');
    try {
      await dispatchSession({
        booking: { id: result.bookingId, customer_name: payload.customerName, duration: 1 },
        rig,
        ffbGainPct: 80,
        pedalProfile: 'medium_load_cell',
        dispatchedBy: user?.email,
      });
      await base44.entities.Booking.update(result.bookingId, { status: 'active' });
      navigate('/venue/kiosk');
    } catch (e) {
      setError(e?.message || 'Could not dispatch the session.');
      setDispatching(false);
    }
  };

  const handlePark = async () => {
    setParking(true);
    setError('');
    try {
      await base44.entities.Booking.update(result.bookingId, { status: 'checked_in' });
      onNewSale();
    } catch (e) {
      setError(e?.message || 'Could not queue the booking.');
      setParking(false);
    }
  };

  const handleEmail = async () => {
    const to = email.trim();
    if (!to) {
      setError("Enter the customer's email address to send the receipt.");
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await base44.functions.invoke('walkInEmailReceipt', {
        orderId: result.orderId,
        email: to,
        phone: phone.trim(),
      });
      if (res.data?.success) {
        setInvoicedTo(res.data.email || to);
        if (res.data.accountName) setLinkedAccount(res.data.accountName);
      } else {
        setError(res.data?.error || 'Could not send the receipt.');
      }
    } catch (e) {
      setError(e?.message || 'Could not send the receipt.');
    }
    setSending(false);
  };

  // A texted receipt is a summary of the same VAT receipt, since SMS cannot carry
  // the invoice PDF. Reuses the shared Twilio sender the booking texts use.
  const handleSms = async () => {
    const to = phone.trim();
    if (!to) {
      setError("Enter the customer's mobile number to text the receipt.");
      return;
    }
    setSmsSending(true);
    setError('');
    try {
      const res = await base44.functions.invoke('sendBookingSms', {
        to,
        message: `Motion Reality VAT receipt #${reference}: ${money(receipt.grossTotal)} total, incl. VAT ${money(receipt.vatTotal)}. Thank you — see you on the grid.`,
      });
      if (res.data?.success) {
        setTextedTo(to);
        // Record the number on the sale and link the customer's account when it
        // matches one, exactly as an emailed receipt does.
        const link = await base44.functions.invoke('walkInEmailReceipt', {
          orderId: result.orderId,
          phone: to,
          saveOnly: true,
        });
        if (link.data?.accountName) setLinkedAccount(link.data.accountName);
      } else {
        setError(res.data?.error || 'Could not send the text receipt.');
      }
    } catch (e) {
      setError(e?.message || 'Could not send the text receipt.');
    }
    setSmsSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-[#111115] border border-white/10 w-full max-w-2xl my-auto"
      >
        {/* Confirmation header */}
        <div className="px-5 py-5 border-b border-white/10 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 220 }}
            className="w-14 h-14 bg-green-600 flex items-center justify-center mx-auto mb-3"
          >
            <CheckCircle2 className="w-7 h-7 text-white" />
          </motion.div>
          <p className="text-green-500 text-[10px] font-bold tracking-widest">PAYMENT RECEIVED</p>
          <p className="text-white font-black text-4xl mt-1">{money(receipt.grossTotal)}</p>
          <p className="text-white/40 text-xs mt-1 font-mono font-bold">#{reference}</p>
        </div>

        <div className="p-5 space-y-5">
          {/* What was sold */}
          {!noSession && (
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-white/10 bg-[#0d0d0f] px-3 py-2.5">
                <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase">Experience</p>
                <p className="text-white font-bold text-sm mt-0.5 truncate">
                  {payload.experienceName || payload.experienceType}
                </p>
              </div>
              <div className="border border-white/10 bg-[#0d0d0f] px-3 py-2.5">
                <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase">Assigned bay</p>
                <p className="text-white font-bold text-sm mt-0.5 truncate">
                  {rig ? `${rig.bay_number} — ${rig.name}` : payload.rigType || '—'}
                </p>
              </div>
            </div>
          )}

          {/* The extra time that actually went on the bays */}
          {extended.length > 0 && (
            <div className="border border-primary/30 bg-primary/[0.06] px-3 py-3">
              <p className="text-primary text-[10px] font-bold tracking-widest uppercase">Session time added</p>
              <div className="mt-2 space-y-1.5">
                {extended.map((e, i) => (
                  <p key={i} className="text-white font-bold text-sm">
                    +{e.minutes} mins on {e.bay_number || e.rig_name}
                  </p>
                ))}
              </div>
            </div>
          )}

          {extensionOnly && extended.length === 0 && (
            <div className="border border-amber-500/40 bg-amber-500/10 px-3 py-3">
              <p className="text-amber-300 font-bold text-sm">No session could be extended</p>
              <p className="text-amber-300/70 text-xs mt-1">
                The bay had stopped running before the payment cleared.
              </p>
            </div>
          )}

          {extensionSkipped.length > 0 && (
            <p className="flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-amber-300 text-xs">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Payment taken, but no extra time was added: {extensionSkipped.map((s) => s.reason).join('; ')}.
              </span>
            </p>
          )}

          <PosReceiptCard
            receipt={receipt}
            reference={reference}
            email={invoicedTo || email || ''}
          />

          {result.stock?.failed > 0 && (
            <p className="flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-amber-300 text-xs">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              Payment taken, but {result.stock.failed} stock line{result.stock.failed > 1 ? 's' : ''} could not be
              updated in Square — adjust them in Stock Control.
            </p>
          )}

          {error && (
            <p className="border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-red-400 text-sm font-bold">
              {error}
            </p>
          )}

          {/* Rig dispatch — nothing starts until staff choose. A sale with no
              session behind it has nothing to start, so this is hidden entirely. */}
          {!noSession && (
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase mb-2">
              {rig ? `${rig.bay_number} is ready — start the session?` : 'Rig dispatch'}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={handleDispatch} disabled={dispatching || parking || !rig} className={GREEN_BTN}>
                {dispatching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                {dispatching ? 'DISPATCHING…' : 'DISPATCH SESSION NOW'}
              </button>
              <button
                onClick={handlePark}
                disabled={dispatching || parking}
                className="flex-1 border-2 border-white/20 hover:border-white/40 disabled:opacity-40 text-white/80 hover:text-white font-black text-sm tracking-widest py-5 flex items-center justify-center gap-2 transition-colors"
              >
                {parking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                START LATER
              </button>
            </div>
            <p className="text-white/30 text-[11px] mt-2">
              Dispatch starts the countdown on the bay and opens the live kiosk view. Start later leaves the booking
              checked in on its bay, ready for the kiosk queue.
            </p>
          </div>
          )}

          {/* Receipt actions — deliberately quieter than dispatch. The three
              receipt buttons hold a fixed three-column row: a button that is
              unavailable (no invoice to email or text) keeps its slot as an empty
              cell, so the row never collapses and PRINT never shifts. */}
          <div className="pt-4 border-t border-white/10">
            <div className="grid grid-cols-3 gap-2">
              {/* A sale with no order behind it has no VAT receipt to send —
                  printing the sale receipt still works. The slot is held either
                  way so the row keeps its three columns. */}
              {result.orderId ? (
                <button
                  onClick={handleEmail}
                  disabled={sending}
                  className="flex items-center justify-center gap-2 border border-white/15 hover:border-white/35 text-white/60 hover:text-white text-xs font-bold tracking-widest px-4 py-3 transition-colors disabled:opacity-40"
                >
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                  EMAIL RECEIPT
                </button>
              ) : (
                <div aria-hidden="true" />
              )}
              {result.orderId ? (
                <button
                  onClick={handleSms}
                  disabled={smsSending}
                  className="flex items-center justify-center gap-2 border border-white/15 hover:border-white/35 text-white/60 hover:text-white text-xs font-bold tracking-widest px-4 py-3 transition-colors disabled:opacity-40"
                >
                  {smsSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                  TEXT RECEIPT
                </button>
              ) : (
                <div aria-hidden="true" />
              )}
              <button
                onClick={() => window.print()}
                className="flex items-center justify-center gap-2 border border-white/15 hover:border-white/35 text-white/60 hover:text-white text-xs font-bold tracking-widest px-4 py-3 transition-colors"
              >
                <Printer className="w-3.5 h-3.5" /> PRINT
              </button>
            </div>

            {/* Where the receipt goes. Always editable so staff can add or correct
                a customer's details at the till — sending then links the sale to
                their account when the email or mobile matches one. */}
            {result.orderId && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-white/40 text-[10px] font-bold tracking-widest uppercase">
                    Receipt email
                  </span>
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="customer@email.com"
                    className="mt-1 w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm px-3 py-3 outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-white/40 text-[10px] font-bold tracking-widest uppercase">Mobile</span>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="07700 900123"
                    className="mt-1 w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm px-3 py-3 outline-none"
                  />
                </label>
              </div>
            )}

            {/* The primary action once payment has cleared — green-600 matches the
                tick badge and the dispatch button, so no new colour is introduced.
                Full width beneath the receipt row so it can never be squeezed. */}
            <button
              onClick={onNewSale}
              className="mt-2 w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-white text-xs font-bold tracking-widest px-4 py-3.5 transition-colors"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> DONE / NEW SALE
            </button>

            {invoicedTo && (
              <p className="flex items-center gap-1.5 text-green-500 text-[11px] font-bold mt-3">
                <CheckCircle2 className="w-3.5 h-3.5" /> Invoiced to {invoicedTo}
              </p>
            )}

            {textedTo && (
              <p className="flex items-center gap-1.5 text-green-500 text-[11px] font-bold mt-3">
                <CheckCircle2 className="w-3.5 h-3.5" /> Receipt texted to {textedTo}
              </p>
            )}

            {linkedAccount && (
              <p className="flex items-center gap-1.5 text-green-500 text-[11px] font-bold mt-3">
                <CheckCircle2 className="w-3.5 h-3.5" /> Linked to account {linkedAccount}
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}